import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  PlusCircle, 
  Clock, 
  CheckCircle, 
  CreditCard, 
  Calendar,
  ArrowRight,
  Video,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const [critiques, setCritiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isActiveSubscriber = user?.subscription_status === 'active' || (
    user?.subscription_status === 'trial' &&
    user?.subscription_end &&
    new Date(user.subscription_end) > new Date()
  );

  useEffect(() => {
    const init = async () => {
      await refreshUser(); // Refresh user data first to get latest credits
      fetchCritiques();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCritiques = async (retryCount = 0) => {
    try {
      setError(false);
      const response = await axios.get(`${API_URL}/api/critiques`, { timeout: 10000 });
      setCritiques(response.data.critiques || []);
    } catch (err) {
      console.error('Failed to fetch critiques:', err);
      if (retryCount < 2) {
        setTimeout(() => fetchCritiques(retryCount + 1), 1000);
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (user?.subscription_status === 'trial') {
      const endDate = user.subscription_end ? new Date(user.subscription_end) : null;
      const daysLeft = endDate ? Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)) : 0;
      if (daysLeft <= 0) {
        return { status: 'Trial Ended', daysLeft: 0, variant: 'destructive' };
      }
      return { status: 'Trial', daysLeft, variant: 'secondary' };
    }
    if (user?.subscription_status === 'active') {
      return { status: 'Active', variant: 'default' };
    }
    return { status: 'Inactive', variant: 'destructive' };
  };

  const subscriptionInfo = getSubscriptionStatus();
  const pendingCritiques = critiques.filter(c => c.status === 'pending');
  const completedCritiques = critiques.filter(c => c.status === 'complete' || c.status === 'completed');

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tight">
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-2">Here's what's happening with your training.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <CreditCard className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="font-display font-bold text-2xl">{user?.credits || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Elevated Credits</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-warning mx-auto mb-2" />
              <p className="font-display font-bold text-2xl">{pendingCritiques.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Pending</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-secondary mx-auto mb-2" />
              <p className="font-display font-bold text-2xl">{completedCritiques.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Completed</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-accent mx-auto mb-2" />
              <Badge variant={subscriptionInfo.variant} className="text-xs">
                {subscriptionInfo.status}
              </Badge>
              {subscriptionInfo.daysLeft !== undefined && subscriptionInfo.daysLeft > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{subscriptionInfo.daysLeft} days left</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Trial Subscription CTA - Prominent banner for trial users */}
        {user?.subscription_status === 'trial' && (
          <Card className="mb-6 border-primary bg-gradient-to-r from-primary/10 to-secondary/10 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg uppercase">
                      {subscriptionInfo.daysLeft > 0 
                        ? `${subscriptionInfo.daysLeft} Days Left in Your Trial`
                        : 'Your Trial Has Ended'}
                    </h3>
                    <p className="text-muted-foreground mt-1">
                      Unlock unlimited access to the community, AI assistant, video library, and marketplace.
                    </p>
                  </div>
                </div>
                <Link to="/subscription" className="flex-shrink-0">
                  <Button size="lg" className="w-full md:w-auto rounded-full font-display uppercase" data-testid="trial-subscribe-btn">
                    Subscribe Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low Credits Warning — only for active subscribers. Non-subs must
            subscribe first before credits are relevant. */}
        {isActiveSubscriber && (user?.credits || 0) <= 1 && (
          <Card className="mb-6 border-warning bg-warning/5">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-warning" />
                <div>
                  <p className="font-semibold">Running low on Elevated Credits</p>
                  <p className="text-sm text-muted-foreground">Get more Elevated Credits to submit critique requests.</p>
                </div>
              </div>
              <Link to="/subscription">
                <Button variant="outline" size="sm" className="rounded-full">
                  Buy Elevated Credits
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Link to="/critique/new" data-testid="new-critique-btn">
            <Card className="card-hover cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <PlusCircle className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg uppercase">New Critique</h3>
                  <p className="text-sm text-muted-foreground">Submit a video for coach review</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </CardContent>
            </Card>
          </Link>

          {isActiveSubscriber ? (
            <Link to="/subscription" data-testid="buy-credits-btn">
              <Card className="card-hover cursor-pointer group">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                    <CreditCard className="w-6 h-6 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-lg uppercase">Buy Elevated Credits</h3>
                    <p className="text-sm text-muted-foreground">Get more Elevated Credits for coaching</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-secondary transition-colors" />
                </CardContent>
              </Card>
            </Link>
          ) : (
            <Link to="/subscription" data-testid="subscribe-cta">
              <Card className="card-hover cursor-pointer group border-primary/40 bg-primary/5">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center group-hover:bg-primary/25 transition-colors">
                    <CreditCard className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-lg uppercase">Subscribe — $25/month</h3>
                    <p className="text-sm text-muted-foreground">Unlock the community, video library, live events and credits.</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        {/* Recent Critiques */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display text-xl uppercase">Recent Critiques</CardTitle>
            <Link to="/critiques" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : critiques.length === 0 ? (
              <div className="text-center py-8">
                <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No critiques yet</p>
                <p className="text-sm text-muted-foreground mt-1">Submit your first video to get started</p>
                <Link to="/critique/new">
                  <Button className="mt-4 rounded-full">
                    Submit Your First Critique
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {critiques.slice(0, 5).map(critique => (
                  <Link 
                    key={critique.id} 
                    to={`/critiques/${critique.id}`}
                    className="block"
                  >
                    <div className="critique-card p-4 rounded-lg border hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge 
                              variant={critique.status === 'complete' || critique.status === 'completed' ? 'default' : 'secondary'}
                              className={critique.status === 'complete' || critique.status === 'completed' ? 'bg-secondary' : 'bg-warning text-foreground'}
                            >
                              {critique.status === 'complete' || critique.status === 'completed' ? 'Completed' : 'Pending'}
                            </Badge>
                            {critique.turnaround === 'priority' && (
                              <Badge variant="outline" className="badge-priority">Priority</Badge>
                            )}
                          </div>
                          <p className="font-medium">{critique.discipline}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">{critique.note}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{formatDate(critique.created_at)}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
